import React from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Virtuoso } from 'react-virtuoso';
import some from 'lodash/some';
import { useHistory } from 'react-router-dom';
import { GroupClientsFiltersPropTypes } from 'utils/PropTypesValues';
import { BRAND_PROFILES } from 'constants/index';

import CreateProfileButton from '../ClientSearchModal/components/CreateProfileButton';
import AddProfileForm from '../ClientSearchModal/components/AddProfileForm';
import Drawer from '../Drawer';
import ClientListItemGeneral from '../ClientListItemGeneral';
import ClientListItemEmpty from '../ClientListItemEmpty';
import AlphabetNavigation from '../AlphabetNavigation';

const STATUS_NOT_LOADED = 0;
const STATUS_LOADING = 1;
const STATUS_LOADED = 2;

const FETCH_CHUNK_SIZE = 10;
// This must be a multiple of FETCH_CHUNK_SIZE
const FIRST_FETCH_SIZE = FETCH_CHUNK_SIZE * 3;

const filtersAreEqual = (currentFilters, filters, sort) => {
  if (currentFilters?.sort !== sort) {
    return false;
  }

  return !some(filters, (filterValue, filterName) => currentFilters[filterName] !== filterValue);
};

const ClientsInfiniteScrollList = ({
  classes,
  clients,
  selectClient,
  contactType,
  itemRenderer,
  customClientClickAction,
  allowClickOnIcon,
  selectedClients,
  selectionEnabled,
  loading,
  clientsStats,
  customerFilterParams,
  saveInfiniteScrollPosition,
  initialFirstVisibleItemIndex,
  lastExternalModifications,
  clearClientsCache,
  useAsSearch,
  recentSearchList,
  setRecentSearch,
  withTopExtraSpace,
  fetch,
  filters,
  displayAlphabet,
  search,
  searchField,
  searchLevel,
  renderEmptySearchLink,
  sort,
  showAddProfileButton,
  groupId,
  groupType,
  customOnCreateNewProfileHandler,
  clearSingleClientCache,
  userBrand,
}) => {
  const currentClientLength = Object.keys(clients).length;
  const isBrandNM = userBrand === BRAND_PROFILES.NM;
  const isEmptySearchState = () => {
    return useAsSearch && search === '';
  };

  const getClientsCount = () => {
    if (isEmptySearchState()) {
      return recentSearchList.length;
    }

    return clientsStats.totalCount;
  };

  const getClientsSource = () => {
    if (isEmptySearchState()) {
      return recentSearchList.reduce((acc, val, i) => {
        acc[i] = val;
        return acc;
      }, {});
    }

    return clients;
  };

  const clientsSource = getClientsSource();
  const clientsCount = getClientsCount();

  const getInitialLoadedState = () => {
    const loadedState = {};
    if (isEmptySearchState()) {
      recentSearchList.forEach((val, index) => {
        loadedState[index] = STATUS_LOADED;
      });
    } else {
      Object.keys(clients).forEach((index) => {
        if (clients[index]) {
          loadedState[index] = STATUS_LOADED;
        }
      });
    }

    return loadedState;
  };

  const listRef = React.useRef();
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [loadedRowsMap, setLoadedRowsMap] = React.useState(getInitialLoadedState());
  const [firstVisibleLetter, setFirstVisibleLetter] = React.useState('A');
  const [firstVisibleIndex, setFirstVisibleIndex] = React.useState(0);

  const hasLoadedClients = () => {
    return !!Object.keys(clientsSource).length;
  };

  const displayEmptySearchText = () => {
    if (useAsSearch && search.length === 1) {
      return true;
    }

    return !hasLoadedClients() && !firstLoad;
  };

  React.useEffect(() => {
    const fromIndex = 0;
    const size = FIRST_FETCH_SIZE;
    if (useAsSearch) {
      if (search.length >= 2) {
        fetch(fromIndex, size, { q: search, field: searchField, searchLevel }).then(() => {
          setFirstLoad(false);
        });
      }

      if (search.length === 0) {
        setLoadedRowsMap(getInitialLoadedState());
      } else {
        clearClientsCache();
        setLoadedRowsMap({});
      }
      return () => {
        clearClientsCache();
        setLoadedRowsMap({});
      };
    }

    const equal = filtersAreEqual(customerFilterParams, filters, sort);
    if (!equal) {
      clearClientsCache();
      setLoadedRowsMap({});
    }

    fetch(fromIndex, size, { sort, filters }).then(() => {
      setFirstLoad(false);
    });
  }, [sort, filters, search, searchField, searchLevel, groupId]);

  // refresh clients list in case of `lastExternalModifications` prop has been updated
  React.useEffect(() => {
    if (!lastExternalModifications) {
      // no need to execute this use effect for initial rendering
      return;
    }

    clearClientsCache();

    let fetchPromise;

    if (useAsSearch) {
      fetchPromise = fetch(0, FIRST_FETCH_SIZE, { q: search, field: searchField, searchLevel });
    } else {
      fetchPromise = fetch(0, FIRST_FETCH_SIZE, { sort, filters });
    }

    setLoadedRowsMap({});
    fetchPromise.then(() => {
      setFirstLoad(false);
    });
  }, [lastExternalModifications]);

  // If page loads from my clients tab, clear cache list
  React.useEffect(() => {
    return () => {
      clearClientsCache();
      setLoadedRowsMap({});
    };
  }, [groupType]);

  const scroll = (letter) => {
    const row = clientsStats.alphabeticalMap[letter];
    setFirstVisibleLetter(letter.toUpperCase());
    listRef.current.scrollToIndex({
      index: row,
      align: 'start',
      behavior: 'auto',
    });
  };

  const shouldRenderAlphabet = React.useMemo(
    () => displayAlphabet && clientsCount > 20 && !!Object.keys(clientsStats.alphabeticalMap).length,
    [displayAlphabet, clientsCount, clientsStats.alphabeticalMap],
  );

  /**
   * Load data in chunks
   *   If chunk size is 10, then the first 3 chunks are 0-9, 10-19, 20-29
   *   If startIndex is 5 and endIndex is 12 then chunks 0-9 and 10-19 will be loaded
   *
   * @param startIndex the first row the list is rendering
   * @param endIndex the last row the list is rendering
   */
  const chunkLoadMoreRows = ({ startIndex, endIndex }) => {
    // Make sure the chunks overlap the start and end
    let chunkStartIndex = startIndex - (startIndex % FETCH_CHUNK_SIZE);
    let chunkEndIndex = endIndex - (endIndex % FETCH_CHUNK_SIZE) + FETCH_CHUNK_SIZE - 1;
    chunkEndIndex = Math.min(chunkEndIndex, getClientsCount() - 1);

    // See if data was already loaded
    while (chunkStartIndex < chunkEndIndex && loadedRowsMap[chunkStartIndex]) {
      chunkStartIndex += FETCH_CHUNK_SIZE;
    }
    const hasMore = chunkStartIndex >= currentClientLength;
    if (chunkStartIndex <= chunkEndIndex && hasMore && !loadedRowsMap[chunkStartIndex]) {
      loadRows(chunkStartIndex, chunkEndIndex);
    }
  };

  const updateLoadedRowsMap = (startIndex, endIndex, status) => {
    const newLoadedRowsMap = { ...loadedRowsMap };
    for (let i = startIndex; i <= endIndex; i += 1) {
      newLoadedRowsMap[i] = status;
    }
    setLoadedRowsMap(newLoadedRowsMap);
  };

  const loadRows = (startIndex, endIndex) => {
    let fetchPromise;

    const from = startIndex;
    const size = endIndex - startIndex + 1;

    updateLoadedRowsMap(startIndex, endIndex, STATUS_LOADING);

    if (search !== undefined) {
      fetchPromise = fetch(from, size, { q: search, field: searchField, searchLevel });
    } else {
      fetchPromise = fetch(from, size, { sort, filters });
    }

    return fetchPromise
      .then(() => {
        updateLoadedRowsMap(startIndex, endIndex, STATUS_LOADED);
      })
      .catch(() => {
        updateLoadedRowsMap(startIndex, endIndex, STATUS_NOT_LOADED);
      });
  };

  const rowsRendered = ({ startIndex }) => {
    if (shouldRenderAlphabet && clients && clients[startIndex]) {
      const startLetter = clients[startIndex].firstName.charAt(0);
      setFirstVisibleLetter(startLetter.toUpperCase());
      setFirstVisibleIndex(startIndex);
    }
  };

  const rangeChanged = ({ startIndex, endIndex }) => {
    const realEndIndex = endIndex > currentClientLength ? currentClientLength : endIndex;
    const realStartIndex = startIndex < currentClientLength ? currentClientLength : startIndex;
    chunkLoadMoreRows({ startIndex: realStartIndex, endIndex: realEndIndex });
    rowsRendered({ startIndex: realStartIndex });
  };

  const beforeGoToProfilePage = (client, index) => {
    if (useAsSearch) {
      setRecentSearch(`${client.firstName} ${client.lastName}`, client);
      return;
    }

    if (saveInfiniteScrollPosition) {
      saveInfiniteScrollPosition(firstVisibleIndex);
      // Since client data can be changed on ClientProfile and ClientConnectDetails pages
      // and we cache Clients List in redux store in order to restore scroll position,
      // we have the issues, when clients data is changed, but old data is shown in
      // ClientInfiniteScrollList component.
      // This action removes single client from the store before going to their profile page.
      // So after restoring scroll position their data will be requested from BE.s
      clearSingleClientCache(index);
    }
  };

  const getRenderItem = () => {
    return itemRenderer || ClientListItemGeneral;
  };

  const rowRenderer = (index) => {
    const client = clientsSource[index];

    const RowComponent = getRenderItem();
    return client ? (
      <RowComponent
        customClientClickAction={customClientClickAction}
        allowClickOnIcon={allowClickOnIcon}
        selectedClients={selectedClients}
        selectClient={selectClient}
        withAlphabet={shouldRenderAlphabet}
        listIndex={index}
        contactType={contactType}
        selectionEnabled={selectionEnabled}
        beforeGoToProfilePage={() => beforeGoToProfilePage(client, index)}
        client={client}
        filters={filters}
        groupType={groupType}
        isBrandNM={isBrandNM}
      />
    ) : (
      <ClientListItemEmpty />
    );
  };

  // useMemo or virtuso might update on each render
  const virtuosoComponents = React.useMemo(() => {
    return {
      // Use a different List wrapper so we can add className
      List: React.forwardRef(({ style, children }, listRef) => (
        <div
          className={shouldRenderAlphabet ? classes.virtualizedList : classes.virtualizedListNoAlphabet}
          style={{ padding: 0, ...style, margin: 0 }}
          ref={listRef}
        >
          {children}
        </div>
      )),
    };
  }, [shouldRenderAlphabet]);

  const [profileFormVisible, setProfileFormVisible] = React.useState(false);
  const history = useHistory();

  if (showAddProfileButton) {
    return (
      <>
        <Drawer
          title="New Client"
          fullWidth
          open={profileFormVisible}
          onClose={() => {
            setProfileFormVisible(false);
          }}
        >
          <AddProfileForm
            searchTerm={search}
            searchBy={searchField}
            history={history}
            customOnCreateNewProfileHandler={customOnCreateNewProfileHandler}
            onClose={() => {
              setProfileFormVisible(false);
            }}
          />
        </Drawer>
        <CreateProfileButton
          onClick={() => {
            setProfileFormVisible(true);
          }}
          className={classes.createProfile}
        />
      </>
    );
  }

  return (
    <>
      {loading ? (
        <CircularProgress data-testid="circular-progress" className={classes.loader} />
      ) : (
        <>
          {isEmptySearchState() && (
            <div className={classes.title} data-testid="recent-clients-header">
              Recent Searches
            </div>
          )}
          <div
            data-testid="scroll-area-wrapper"
            className={`${withTopExtraSpace ? classes.scrollAreaWrapperWithTopSpace : classes.scrollAreaWrapper}
          ${isEmptySearchState() ? classes.scrollAreaWithRecentSearches : ''}
          `}
          >
            {clientsCount ? (
              <>
                <div
                  className={shouldRenderAlphabet ? classes.listWrapper : classes.listWrapperNoAlphabet}
                  data-testid="clients-list-wrapper"
                >
                  <Virtuoso
                    ref={listRef}
                    totalCount={getClientsCount()}
                    itemContent={rowRenderer}
                    initialTopMostItemIndex={initialFirstVisibleItemIndex}
                    rangeChanged={rangeChanged}
                    overscan={300}
                    defaultItemHeight={72}
                    components={virtuosoComponents}
                  />
                </div>
                {shouldRenderAlphabet && (
                  <AlphabetNavigation
                    alphabeticalMap={clientsStats.alphabeticalMap}
                    activeLetter={firstVisibleLetter}
                    setActiveLetter={setFirstVisibleLetter}
                    scroll={scroll}
                  />
                )}
              </>
            ) : (
              displayEmptySearchText() && (
                <div className={classes.empty} data-testid="no-records-found">
                  No results
                  {renderEmptySearchLink()}
                </div>
              )
            )}
          </div>
        </>
      )}
    </>
  );
};

ClientsInfiniteScrollList.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  clients: PropTypes.shape({
    firstName: PropTypes.string,
    profileId: PropTypes.string,
  }),
  loading: PropTypes.bool,
  displayAlphabet: PropTypes.bool,
  selectClient: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({ profileId: PropTypes.string })),
  clientsStats: PropTypes.shape({
    alphabeticalMap: PropTypes.objectOf(PropTypes.number),
    totalCount: PropTypes.number,
  }),
  customerFilterParams: PropTypes.objectOf(PropTypes.any),
  saveInfiniteScrollPosition: PropTypes.func,
  initialFirstVisibleItemIndex: PropTypes.number,
  clearClientsCache: PropTypes.func,
  clearSingleClientCache: PropTypes.func,
  fetch: PropTypes.func,
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({}),
  }),
  filters: GroupClientsFiltersPropTypes,
  sort: PropTypes.string,
  lastExternalModifications: PropTypes.number,
  recentSearchList: PropTypes.arrayOf(PropTypes.any),
  showAddProfileButton: PropTypes.bool,
  withTopExtraSpace: PropTypes.bool,
  groupId: PropTypes.string,
  renderEmptySearchLink: PropTypes.func,
  customOnCreateNewProfileHandler: PropTypes.func,
};

ClientsInfiniteScrollList.defaultProps = {
  classes: {},
  clients: {},
  clientsStats: {},
  lastExternalModifications: null,
  displayAlphabet: true,
  initialFirstVisibleItemIndex: 0,
  filters: {},
  withTopExtraSpace: false,
  sort: '',
  fetch: () => null,
  recentSearchList: [],
  groupId: '',
  renderEmptySearchLink: () => null,
  customOnCreateNewProfileHandler: () => null,
};

export default ClientsInfiniteScrollList;
